import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  addCompanyCredits,
  fetchCompanyCreditBalance,
  fetchCompanyCreditTransactions,
} from '@/api/(admin)/credits';
import type { AddCompanyCreditsPayload } from '@/api/(admin)/credits/types';
import { useTranslator } from '@/hooks/useTranslator';

interface Args {
  companyId: string;
  /** 1-based, as the table counts pages. */
  page: number;
  limit: number;
}

/**
 * A company's credit balance, its ledger, and the top-up.
 *
 * The ledger now pages 0-based off `metadata.pagination`, the same convention
 * as the roster and network endpoints — converted here since the table still
 * counts pages from 1.
 */
/** The balance cards. Shared by the tab's route loader and the hook. */
export function companyCreditBalanceQueryOptions(companyId: string) {
  return queryOptions({
    queryKey: ['admin', 'company-credit-balance', companyId] as const,
    queryFn: () => fetchCompanyCreditBalance(companyId),
    enabled: Boolean(companyId),
    staleTime: 60_000,
  });
}

/** The ledger. Shared by the tab's route loader and the hook. */
export function companyCreditTransactionsQueryOptions({
  companyId,
  page,
  limit,
}: Args) {
  const params = new URLSearchParams({
    page: String(Math.max(0, page - 1)),
    count: String(limit),
  });

  return queryOptions({
    queryKey: [
      'admin',
      'company-credit-transactions',
      companyId,
      page,
      limit,
    ] as const,
    queryFn: () => fetchCompanyCreditTransactions(companyId, params.toString()),
    enabled: Boolean(companyId),
    staleTime: 60_000,
  });
}

export function useCompanyCredits({ companyId, page, limit }: Args) {
  const { getTranslation: t } = useTranslator({ page: 'managementCompanies' });
  const queryClient = useQueryClient();

  const balanceQuery = useQuery(companyCreditBalanceQueryOptions(companyId));
  const transactionsQuery = useQuery(
    companyCreditTransactionsQueryOptions({ companyId, page, limit }),
  );

  const addMutation = useMutation({
    mutationFn: (payload: AddCompanyCreditsPayload) =>
      addCompanyCredits(companyId, payload),
  });

  async function add(payload: AddCompanyCreditsPayload): Promise<boolean> {
    try {
      await addMutation.mutateAsync(payload);
      toast.success(t('detail.credits.added'));
      // Both: a top-up moves the balance and writes a ledger row.
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'company-credit-balance', companyId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'company-credit-transactions', companyId],
      });
      return true;
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t('errors.creditsAddFailed'),
      );
      return false;
    }
  }

  const balance = balanceQuery.data?.data;
  const pagination = transactionsQuery.data?.metadata?.pagination;

  return {
    balance: balance?.balance ?? 0,
    reserved: balance?.reserved ?? 0,
    consumed: balance?.consumed ?? 0,
    /** What is left to spend once the reserved part is held back. */
    available: (balance?.balance ?? 0) - (balance?.reserved ?? 0),
    isLoadingBalance: balanceQuery.isLoading,
    rows: transactionsQuery.data?.data ?? [],
    totalCount: pagination?.total ?? transactionsQuery.data?.data?.length ?? 0,
    isLoadingTransactions: transactionsQuery.isLoading,
    isError: balanceQuery.isError || transactionsQuery.isError,
    add,
    isAdding: addMutation.isPending,
  };
}
